import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MagnifyingGlassIcon, CloudArrowUpIcon, DocumentTextIcon, UserIcon } from '@heroicons/react/24/outline';

const cvStats = {
  newUploads: 25,
  totalCVs: 15000,
  activeCandidates: 340,
};

const topSkills = [
  { skill: 'Python', count: 1200 },
  { skill: 'SQL', count: 950 },
  { skill: 'Banking Ops', count: 800 },
  { skill: 'JavaScript', count: 700 },
  { skill: 'Data Analysis', count: 650 },
];

const CVBank = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className="p-6 space-y-8"
    >
      <h1 className="text-3xl font-extrabold text-gray-800">CV Bank Management</h1>
      
      {/* 🔹 Search and Stats Section */}
      <section>
        <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200 space-y-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full rounded-full border-0 py-3 pl-10 pr-4 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              placeholder="Search by skill, role, experience..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div 
              className="p-6 rounded-xl bg-indigo-50 flex flex-col items-center justify-center text-center"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              <DocumentTextIcon className="h-10 w-10 text-indigo-600 mb-2" />
              <p className="text-gray-500 font-medium text-sm">NEW UPLOADS TODAY</p>
              <h3 className="text-4xl font-extrabold text-indigo-800 mt-1">{cvStats.newUploads}</h3>
            </motion.div>
            <motion.div 
              className="p-6 rounded-xl bg-teal-50 flex flex-col items-center justify-center text-center"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <CloudArrowUpIcon className="h-10 w-10 text-teal-600 mb-2" />
              <p className="text-gray-500 font-medium text-sm">TOTAL CV BANK SIZE</p>
              <h3 className="text-4xl font-extrabold text-teal-800 mt-1">{cvStats.totalCVs.toLocaleString()}</h3>
            </motion.div>
            <motion.div 
              className="p-6 rounded-xl bg-blue-50 flex flex-col items-center justify-center text-center"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <UserIcon className="h-10 w-10 text-blue-600 mb-2" />
              <p className="text-gray-500 font-medium text-sm">ACTIVE CANDIDATES IN PIPELINE</p>
              <h3 className="text-4xl font-extrabold text-blue-800 mt-1">{cvStats.activeCandidates}</h3>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 🔹 Top Skills and Candidate List */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 bg-white p-6 rounded-2xl shadow-lg border border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Top Skills Available</h2>
          <ul className="space-y-4">
            {topSkills.map((item, index) => (
              <motion.li 
                key={item.skill} 
                className="flex items-center justify-between"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * index }}
              >
                <span className="text-lg font-medium text-gray-700">{item.skill}</span>
                <span className="px-3 py-1 text-sm font-semibold rounded-full bg-gray-200 text-gray-800">{item.count}</span>
              </motion.li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-2 bg-white p-6 rounded-2xl shadow-lg border border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Candidates</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Candidate Name</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Primary Skill</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Experience</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">John Smith</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Python</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">5 Years</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Available</span>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Jane Doe</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">SQL</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">8 Years</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">In Pipeline</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default CVBank;